import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { Box, Button, Drawer, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { API_BASE_URL } from "../../config";
import { ReactComponent as Gate3DecisionMemoIcon } from "../../asserts/logo/Gate3_DecisionMemo.svg";
import { ReactComponent as LaunchReadinessPackIcon } from "../../asserts/logo/LaunchReadinessPack.svg";
import { PRIMARY_BTN_SX } from "../gate2/gate2Tokens";
import { decisionAuthorityAccountable, decisionAuthorityConsulted } from "./Gate3Decision";

const FONT = "Lato, sans-serif";

const OUTPUT_ICONS = {
  memo: Gate3DecisionMemoIcon,
  readiness: LaunchReadinessPackIcon,
};

function OutputRowIcon({ name }) {
  const Icon = OUTPUT_ICONS[name];
  if (!Icon) return null;
  return <Icon aria-hidden style={{ width: 32, height: 32, flexShrink: 0, display: "block" }} />;
}

const VERDICT_LABEL = {
  go: "Go",
  conditional_go: "Conditional Go",
  no_go: "No-Go",
};

const FLYOUTS = {
  memo: {
    title: "Gate 3 Decision Memo",
    subtitle: "Recorded verdict, conditions, decision authority, and timestamp.",
  },
  readiness: {
    title: "Launch Readiness Pack section",
    subtitle: "Monitor and runbook pointers bundled for this release.",
  },
};

const ROW_LABEL_SX = {
  fontFamily: FONT,
  fontWeight: 400,
  fontSize: { xs: 12, lg: 12 },
  lineHeight: { xs: "16px", lg: "18px" },
  letterSpacing: 0,
  color: "#707070",
};

const ROW_VALUE_SX = {
  fontFamily: FONT,
  fontWeight: 700,
  fontSize: { xs: 13, sm: 14, lg: 14 },
  lineHeight: { xs: "20px", lg: "21px" },
  letterSpacing: 0,
  color: "#383838",
  overflowWrap: "anywhere",
};

function FlyoutField({ label, value }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", minWidth: 0 }}>
      <Typography sx={ROW_LABEL_SX}>{label}</Typography>
      <Typography sx={ROW_VALUE_SX}>{value}</Typography>
    </Box>
  );
}

function OutputFlyout({ spec, onClose }) {
  if (!spec) return null;
  return (
    <Drawer
      anchor="right"
      open
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 480, lg: 520 },
          maxWidth: "100vw",
          height: "100%",
          bgcolor: "#FFFFFF",
          boxShadow: "-8px 0 32px rgba(16, 24, 40, 0.14)",
        },
      }}
      slotProps={{
        backdrop: { sx: { bgcolor: "rgba(52, 64, 84, 0.45)" } },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minWidth: 0, bgcolor: "#FFFFFF" }}>
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            px: { xs: "16px", lg: "24px" },
            py: { xs: "14px", lg: "16px" },
            borderBottom: "1px solid #E6E6E6",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: { xs: 15, sm: 16, lg: 16 }, lineHeight: { xs: "22px", lg: "24px" }, letterSpacing: 0, color: "#383838" }}>
                {spec.title}
              </Typography>
              <Box
                component="span"
                sx={{
                  boxSizing: "border-box",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 20,
                  px: "8px",
                  borderRadius: "12px",
                  border: "1px solid #8A5C00",
                  bgcolor: "#FFF8E0",
                  color: "#8A5C00",
                  fontFamily: FONT,
                  fontWeight: 700,
                  fontSize: 12,
                  lineHeight: 1,
                  letterSpacing: 0,
                  whiteSpace: "nowrap",
                }}
              >
                {spec.pill}
              </Box>
            </Box>
            <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: { xs: 12, sm: 13, lg: 13 }, lineHeight: { xs: "16px", lg: "18px" }, letterSpacing: 0, color: "#707070", mt: "4px" }}>
              {spec.subtitle}
            </Typography>
          </Box>
          <IconButton aria-label="Close" onClick={onClose} sx={{ color: "#707070", p: "4px", mt: "-2px" }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: { xs: "16px", lg: "24px" }, py: { xs: "12px", lg: "16px" } }}>
          {spec.rows.map(([label, value], index) => (
            <Box
              key={label}
              sx={{
                py: { xs: "10px", lg: "12px" },
                borderBottom: index === spec.rows.length - 1 && !spec.section ? "none" : "1px solid #E6E6E6",
              }}
            >
              <FlyoutField label={label} value={value} />
            </Box>
          ))}
          {spec.section ? (
            <Box sx={{ pt: { xs: "16px", lg: "20px" } }}>
              <Typography sx={{ ...ROW_VALUE_SX, mb: { xs: "12px", lg: "16px" } }}>{spec.section.title}</Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: "12px", lg: "16px" } }}>
                {spec.section.rows.map(([label, value]) => (
                  <FlyoutField key={label} label={label} value={value} />
                ))}
              </Box>
            </Box>
          ) : null}
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            justifyContent: "flex-end",
            px: { xs: "16px", lg: "24px" },
            py: { xs: "12px", lg: "16px" },
            borderTop: "1px solid #E6E6E6",
            bgcolor: "#FFFFFF",
          }}
        >
          <Button variant="contained" disableElevation onClick={onClose} sx={{ ...PRIMARY_BTN_SX, width: { xs: "100%", sm: "auto" }, minWidth: { sm: 72 } }}>
            Done
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}

const OUTPUTS = [
  {
    title: "Gate 3 Decision Memo",
    detail: "Recorded verdict, conditions, decision authority, and timestamp.",
    kind: "Decision record",
    status: "Generated",
    icon: "memo",
  },
  {
    title: "Launch Readiness Pack",
    detail: "Monitoring and operational runbook references for release.",
    kind: "Operational package",
    status: "Generated",
    icon: "readiness",
  },
];

function SectionCard({ children, sx }) {
  return (
    <Box
      sx={{
        border: "1px solid #E6E6E6",
        borderRadius: { xs: "8px", lg: "12px" },
        bgcolor: "#FFFFFF",
        p: { xs: 1.5, sm: 2, lg: 2.5 },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function OutcomeStrip() {
  return (
    <SectionCard
      sx={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        columnGap: { xs: 1.25, sm: 1.5, lg: 2 },
        rowGap: { xs: 1, sm: 1.25, lg: 1.5 },
        py: { xs: 1.25, sm: 1.5, lg: 1.75 },
      }}
    >
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: { xs: 1, sm: 1.25, lg: 1.5 }, minWidth: 0, flex: "1 1 220px" }}>
        <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: { xs: 13, sm: 13, lg: 14 }, lineHeight: { xs: 1.25, lg: 1 }, letterSpacing: 0, color: "#383838" }}>
          Evaluation Outcome
        </Typography>
        <Box
          component="span"
          sx={{
            boxSizing: "border-box",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: { xs: "auto", lg: 97 },
            height: { xs: 18, sm: 20, lg: 20 },
            px: "8px",
            gap: "2px",
            borderRadius: "12px",
            border: "1px solid #8A5C00",
            bgcolor: "#FFF8E0",
            color: "#8A5C00",
            fontFamily: FONT,
            fontWeight: 700,
            fontSize: { xs: 11, sm: 12, lg: 12 },
            lineHeight: 1,
            letterSpacing: 0,
            whiteSpace: "nowrap",
          }}
        >
          Conditional Go
        </Box>
        <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: { xs: 12, sm: 13, lg: 14 }, lineHeight: { xs: 1.25, lg: 1 }, letterSpacing: 0, color: "#707070" }}>
          The application may proceed under a logged condition.
        </Typography>
      </Box>
      <Box sx={{ display: "flex", flexWrap: { xs: "wrap", sm: "nowrap" }, alignItems: "center", flex: { xs: "1 1 100%", sm: "0 0 auto" }, gap: { xs: 1, sm: 0 } }}>
        {[
          ["0", "Hard Blockers"],
          ["1", "Open Gap"],
          ["1", "Required Exception"],
        ].map(([count, label], index) => (
          <Box key={label} sx={{ display: "flex", alignItems: "center" }}>
            {index > 0 ? (
              <Box aria-hidden sx={{ display: { xs: "none", sm: "block" }, width: "1px", height: 16, bgcolor: "#E6E6E6", mx: { sm: 1.25, lg: 1.75 } }} />
            ) : null}
            <Typography component="span" sx={{ fontFamily: FONT, letterSpacing: 0, whiteSpace: "nowrap" }}>
              <Box component="span" sx={{ fontWeight: 700, fontSize: { xs: 14, sm: 15, lg: 16 }, lineHeight: { xs: 1.15, lg: 1 }, color: "#383838" }}>
                {count}
              </Box>
              <Box component="span" sx={{ fontWeight: 400, fontSize: { xs: 12, sm: 13, lg: 14 }, lineHeight: { xs: 1.15, lg: 1 }, color: "#707070" }}>
                {` ${label}`}
              </Box>
            </Typography>
          </Box>
        ))}
      </Box>
    </SectionCard>
  );
}

function filledOrDash(value) {
  const text = String(value || "").trim();
  return text || "—";
}

function memoFlyout(applicationName, verdict, verdictLabel, updated, exception) {
  const spec = {
    ...FLYOUTS.memo,
    pill: verdictLabel,
    rows: [
      ["System", applicationName || "—"],
      ["Verdict", verdictLabel],
      ["Accountable approver", decisionAuthorityAccountable()],
      ["Consulted", decisionAuthorityConsulted()],
      ["Updated", updated || "—"],
    ],
  };
  if (verdict === "conditional_go") {
    spec.section = {
      title: "Post-launch sampling plan defined",
      rows: [
        ["Condition", filledOrDash(exception.condition)],
        ["Control", filledOrDash(exception.control)],
        ["Expiry", filledOrDash(exception.expiry)],
      ],
    };
  }
  return spec;
}

function cadenceAnswer(checklist, customDays) {
  const selected = String(checklist.cadence || "").trim();
  if (!selected) return "—";
  if (selected === "Custom recurring day") {
    const days = String(customDays || "").trim();
    return days ? `Every ${days} days` : "Custom recurring day";
  }
  return selected;
}

function readinessFlyout(verdictLabel, checklist, customDays) {
  const answer = (key) => filledOrDash(checklist[key]);
  return {
    ...FLYOUTS.readiness,
    pill: verdictLabel,
    rows: [
      ["Monitoring dashboards/monitors linked", answer("monitors")],
      ["Alert thresholds defined", answer("thresholds")],
      ["Incident workflow defined", answer("incident")],
      ["Rollback / kill-switch plan defined", answer("rollback")],
      ["Post-launch sampling plan defined", answer("sampling")],
      ["Trust review cadence set", cadenceAnswer(checklist, customDays)],
      ["Action-trace capture (agentic systems only)", answer("trace")],
    ],
  };
}

export default function Gate3Outputs({
  verdict = "conditional_go",
  updated = "",
  exception = { condition: "", control: "", expiry: "" },
  checklist = {},
  customDays = "",
}) {
  const { projectId } = useParams();
  const storedName = useSelector((state) => state.project.currentProjectName);
  const [applicationName, setApplicationName] = useState(storedName || "");
  const [openId, setOpenId] = useState(null);
  const verdictLabel = VERDICT_LABEL[verdict] || VERDICT_LABEL.conditional_go;

  useEffect(() => {
    if (storedName) setApplicationName(storedName);
  }, [storedName]);

  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;
    axios
      .get(`${API_BASE_URL}/projects/${projectId}`)
      .then((res) => {
        if (cancelled) return;
        const row = res?.data?.data;
        const name = String(row?.gate0_data?.app_name || row?.project_name || "").trim();
        if (name) setApplicationName(name);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const openSpec =
    openId === "memo"
      ? memoFlyout(applicationName, verdict, verdictLabel, updated, exception)
      : openId === "readiness"
        ? readinessFlyout(verdictLabel, checklist, customDays)
        : null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.25, sm: 1.5, lg: 2 }, width: "100%", maxWidth: { xs: "100%", lg: 1012 } }}>
      <OutcomeStrip />

      <SectionCard sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.25, lg: 1.5 }, p: 0, py: { xs: 1.5, lg: 2 } }}>
        <Box sx={{ px: "16px" }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: { xs: 13, sm: 14, lg: 14 }, lineHeight: { xs: "18px", lg: "21px" }, letterSpacing: 0, color: "#383838" }}>
            Generated Outputs
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: { xs: 12, sm: 13, lg: 13 }, lineHeight: { xs: "16px", lg: "18px" }, letterSpacing: 0, color: "#707070", mt: 0.5 }}>
            Generated automatically from the recorded Gate 3 decision and supporting evidence.
          </Typography>
        </Box>
        <Box>
          {OUTPUTS.map((item, index) => (
            <Box
              key={item.title}
              sx={{
                boxSizing: "border-box",
                width: "100%",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "center" },
                justifyContent: "space-between",
                gap: { xs: 0.75, sm: 1.5 },
                px: "16px",
                py: "12px",
                borderBottom: index === OUTPUTS.length - 1 ? "none" : "1px solid #E6E6E6",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, minWidth: 0 }}>
                <OutputRowIcon name={item.icon} />
                <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "3px" }}>
                  <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: { xs: 12, sm: 12, lg: 13 }, lineHeight: { xs: 1.25, lg: 1 }, letterSpacing: 0, color: "#383838" }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: { xs: 12, sm: 12, lg: 13 }, lineHeight: { xs: 1.25, lg: 1 }, letterSpacing: 0, color: "#707070" }}>
                    {item.detail}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 2, lg: 4 }, alignSelf: { xs: "stretch", sm: "center" }, justifyContent: "space-between", flexShrink: 0 }}>
                <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                  <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: { xs: 10, lg: 10 }, lineHeight: 1, letterSpacing: 0, color: "#707070" }}>{item.kind}</Typography>
                  <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: { xs: 12, sm: 12, lg: 13 }, lineHeight: { xs: 1.25, lg: 1 }, letterSpacing: 0, color: "#383838" }}>{item.status}</Typography>
                </Box>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setOpenId(item.icon)}
                  sx={{ border: "none", bgcolor: "transparent", p: 0, cursor: "pointer", fontFamily: FONT, fontWeight: 700, fontSize: { xs: 12, sm: 13, lg: 13 }, lineHeight: { xs: "20px", lg: "24px" }, letterSpacing: 0, verticalAlign: "middle", color: "#005BF0" }}
                >
                  View
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </SectionCard>

      <SectionCard>
        <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: { xs: 14, lg: 16 }, lineHeight: 1.25, color: "#383838" }}>
          Active Exception
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mt: 1.25 }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: { xs: 13, lg: 14 }, lineHeight: 1.25, color: "#383838" }}>
            Post-launch sampling plan defined
          </Typography>
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", height: 20, px: "8px", borderRadius: "12px", border: "1px solid #8A5C00", bgcolor: "#FFF8E0", color: "#8A5C00", fontFamily: FONT, fontWeight: 700, fontSize: 12, lineHeight: 1 }}>
            Exception required
          </Box>
        </Box>
        <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: { xs: 13, lg: 14 }, lineHeight: 1.25, color: "#383838", mt: 1.5 }}>
          Active Exception
        </Typography>
        <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: { xs: 13, lg: 14 }, lineHeight: 1.3, color: "#383838", mt: 0.5 }}>
          Post-launch sampling plan defined
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
            gap: { xs: 1.25, lg: 2 },
            mt: 1.5,
            p: { xs: 1.25, lg: 1.5 },
            border: "1px solid #E6E6E6",
            borderRadius: "8px",
          }}
        >
          {[
            ["Condition", "Proceed while sampling coverage is formalized"],
            ["Control", "Weekly post-launch sample review by Quality"],
            ["Expiry", "Review within 30 days of launch"],
          ].map(([label, value]) => (
            <Box key={label}>
              <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: 12, lineHeight: 1.3, color: "#707070" }}>{label}</Typography>
              <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: { xs: 13, lg: 14 }, lineHeight: 1.35, color: "#383838", mt: 0.5 }}>{value}</Typography>
            </Box>
          ))}
        </Box>
        <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: { xs: 12, lg: 13 }, lineHeight: 1.4, color: "#707070", mt: 1.5 }}>
          Carries into Decision: Checklist Gap → Exception → Control → Decision
        </Typography>
      </SectionCard>

      <SectionCard>
        <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: { xs: 14, lg: 16 }, lineHeight: 1.25, color: "#383838" }}>
          Version History
        </Typography>
        <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: { xs: 12, lg: 14 }, lineHeight: 1.35, color: "#707070", mt: 0.5 }}>
          Recorded Gate 3 decisions and exceptions remain available for audit.
        </Typography>
        <Box sx={{ mt: 1.5, border: "1px solid #E6E6E6", borderRadius: "8px", overflow: "hidden" }}>
          {[
            ["Decision v1", "Conditional Go · Head of Quality"],
            ["Exception", "Post-launch sampling plan defined · Expires 30 days after launch"],
          ].map(([label, value], index) => (
            <Box
              key={label}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "160px 1fr" },
                gap: 1,
                px: { xs: 1.25, lg: 2 },
                py: { xs: 1.25, lg: 1.5 },
                borderTop: index === 0 ? "none" : "1px solid #E6E6E6",
              }}
            >
              <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: { xs: 13, lg: 14 }, color: "#383838" }}>{label}</Typography>
              <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: { xs: 13, lg: 14 }, color: "#383838" }}>{value}</Typography>
            </Box>
          ))}
        </Box>
        <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: { xs: 12, lg: 13 }, lineHeight: 1.4, color: "#707070", mt: 1.5 }}>
          Previous decision records are retained for audit and are never overwritten.
        </Typography>
      </SectionCard>

      <SectionCard>
        <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: { xs: 14, lg: 16 }, lineHeight: 1.25, color: "#383838" }}>
          Gate 3 complete
        </Typography>
        <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: { xs: 12, lg: 14 }, lineHeight: 1.4, color: "#707070", mt: 0.75 }}>
          The Conditional Go decision has been recorded and its required exception will remain tracked through expiry.
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: { xs: 1.5, lg: 2 }, mt: { xs: 1.5, lg: 2 } }}>
          {[
            ["Release status", "Conditional Go"],
            ["Exception tracking", "1 active exception"],
            ["Next governance reference", "Gate 4"],
          ].map(([label, value]) => (
            <Box key={label}>
              <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: 12, lineHeight: 1.3, color: "#707070" }}>{label}</Typography>
              <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: { xs: 14, lg: 16 }, lineHeight: 1.3, color: "#383838", mt: 0.5 }}>{value}</Typography>
            </Box>
          ))}
        </Box>
      </SectionCard>
      <OutputFlyout spec={openSpec} onClose={() => setOpenId(null)} />
    </Box>
  );
}
